import { z } from "zod";

/** Canonical order matches the handoff picker: landscape, vertical, portrait, square. */
export const TARGET_RATIO_ORDER = ["16:9", "9:16", "4:5", "1:1"] as const;

export const TargetRatioSchema = z.enum(TARGET_RATIO_ORDER);
export type TargetRatio = z.infer<typeof TargetRatioSchema>;

export const TARGET_RATIO_LABELS: Record<TargetRatio, string> = {
  "16:9": "Landscape",
  "9:16": "Vertical",
  "4:5": "Portrait",
  "1:1": "Square",
};

/** Stored platform ids are lowercase (`google` is YouTube, `meta` is Meta). */
export type TargetRatioPlatformFamily = "youtube" | "meta" | "other";

const YOUTUBE_PLATFORM_IDS = new Set(["google", "youtube"]);
const META_PLATFORM_IDS = new Set(["meta", "facebook", "instagram"]);

export const YOUTUBE_TARGET_RATIOS: readonly TargetRatio[] = TARGET_RATIO_ORDER;
export const META_TARGET_RATIOS: readonly TargetRatio[] = ["9:16", "4:5", "1:1"];

/**
 * Write path. Accepts a known list and rejects unknown or duplicate values.
 * A missing field becomes [] only when a caller uses the default; create
 * input uses a separate required schema.
 */
export const TargetRatiosFieldSchema = z
  .array(TargetRatioSchema)
  .superRefine((ratios, ctx) => {
    if (new Set(ratios).size !== ratios.length) {
      ctx.addIssue({
        code: "custom",
        message: "targetRatios must be unique",
      });
    }
  })
  .default([]);

function platformIds(
  adPlatforms: readonly string[] | string | null | undefined,
): string[] {
  const raw = Array.isArray(adPlatforms)
    ? adPlatforms
    : adPlatforms == null
      ? []
      : [adPlatforms];
  return raw
    .map((platform) => platform.trim().toLowerCase())
    .filter((platform) => platform.length > 0);
}

export function classifyAdPlatform(
  adPlatforms: readonly string[] | string | null | undefined,
): TargetRatioPlatformFamily {
  const ids = platformIds(adPlatforms);
  if (ids.some((platform) => YOUTUBE_PLATFORM_IDS.has(platform))) return "youtube";
  if (ids.some((platform) => META_PLATFORM_IDS.has(platform))) return "meta";
  return "other";
}

export function defaultTargetRatiosForPlatforms(
  adPlatforms: readonly string[] | string | null | undefined,
): TargetRatio[] {
  const family = classifyAdPlatform(adPlatforms);
  if (family === "youtube") return [...YOUTUBE_TARGET_RATIOS];
  if (family === "meta") return [...META_TARGET_RATIOS];
  return [];
}

export function canonicalTargetRatios(ratios: readonly TargetRatio[]): TargetRatio[] {
  const selected = new Set(ratios);
  return TARGET_RATIO_ORDER.filter((ratio) => selected.has(ratio));
}

/**
 * Read path for a stored snapshot. One unknown or repeated value must not
 * discard the brief, so unknown entries drop and the rest are deduped.
 */
export const StoredTargetRatiosSchema = z.preprocess((value) => {
  if (!Array.isArray(value)) return [];
  const known: TargetRatio[] = [];
  for (const item of value) {
    const parsed = TargetRatioSchema.safeParse(item);
    if (parsed.success) known.push(parsed.data);
  }
  return canonicalTargetRatios(known);
}, z.array(TargetRatioSchema));

export function targetRatiosMatchDefault(
  ratios: readonly TargetRatio[],
  family: TargetRatioPlatformFamily,
): boolean {
  const defaults =
    family === "youtube"
      ? YOUTUBE_TARGET_RATIOS
      : family === "meta"
        ? META_TARGET_RATIOS
        : [];
  if (defaults.length === 0) return false;
  const selected = canonicalTargetRatios(ratios);
  return (
    selected.length === defaults.length &&
    selected.every((ratio, index) => ratio === defaults[index])
  );
}

/** Each selected ratio is one cut of every script. Pricing stays out of this count. */
export function countTargetRatioDeliverables(
  scriptCount: number,
  ratios: readonly TargetRatio[],
): number {
  if (scriptCount < 0 || ratios.length === 0) return 0;
  return scriptCount * ratios.length;
}
